import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Typography, 
  CircularProgress 
} from '@mui/material';
import { fetchAttachmentsByIds } from '../../utils/api';
import AttachmentCard from './AttachmentCard';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

const AttachmentViewerS3 = ({ 
  attachments = [], 
  canEdit = false, 
  onDownload, 
  customActions = [],
  onDelete, // New prop for custom delete handling
}) => {
  const [attachmentDetails, setAttachmentDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAttachments = async () => {
      if (attachments.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        // Extract attachment IDs
        const attachmentIds = attachments.map(att => {
          if (typeof att === 'string') return att;
          if (att && typeof att === 'object' && att.id) return att.id;
          return null;
        }).filter(id => id !== null);
        
        if (attachmentIds.length === 0) {
          setLoading(false);
          return;
        }
        
        // Fetch attachment details
        const attachmentResults = await fetchAttachmentsByIds(attachmentIds);
        
        if (attachmentResults && attachmentResults.length > 0) {
          
          // Convert to object for easy lookup
          const detailsObject = {};
          attachmentResults.forEach(att => {
            detailsObject[att.id] = att;
          });
          setAttachmentDetails(detailsObject);
        }
      } catch (error) {
        console.error('Error fetching attachments:', error);
        setError('Failed to load attachments');
      } finally {
        setLoading(false);
      }
    };

    fetchAttachments();
  }, [attachments]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress size={24} />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading attachments...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" p={4}>
        {error}
      </Typography>
    );
  }

  if (attachments.length === 0) {
    return (
      <>
      <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80px'}}> 
        <Typography variant="context" color="textSecondary" align="center" sx={{ mt: 4, pb: 4 , display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          <InformationCircleIcon  className="icon-lg" style={{   color: '#64748b', marginRight: 8 }} />
          No attachments available
        </Typography> 
        </Box>
      </>
    ); 
  }

  // Get valid attachments with file_key
  const validAttachments = Object.values(attachmentDetails).filter(
    attachment => attachment && attachment.file_key
  );

  if (validAttachments.length === 0) {
    return (
      <Typography color="textSecondary" align="center" sx={{ mt: 4 }}>
        No valid attachments found
      </Typography>
    );
  }

  return (
    <Box className='mt-6 pb-6'>
      <Grid container spacing={2}>
        {validAttachments.map((attachment) => (
          <Grid item xs={12} sm={6} md={4} key={attachment.id}>
            <AttachmentCard
              attachment={attachment}
              canEdit={canEdit}
              onDownload={onDownload}
              customActions={customActions}
              onDelete={onDelete} // Pass the new prop
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default AttachmentViewerS3; 